import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Wallet, CalendarClock, CalendarDays, PenLine, UserPlus, ListChecks, FolderPlus, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { NewCaseModal } from '../NewCaseModal';
import { AkteMandanten } from './AkteMandanten';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useAuth, api } from '../../context/AuthContext';
import { ErfassungEinladenDialog } from './ErfassungEinladenDialog';

const AKTIONEN = [
  { id: 'akte', label: 'Akte anlegen', icon: FolderPlus },
  { id: 'beteiligter', label: 'Weiteren Mandanten zur Akte', icon: Users, akte: true },
  { id: 'akonto', label: 'Akonto anfordern', icon: Wallet, to: '/kanzlei/zahlungen/akonto', zahlung: true },
  { id: 'raten', label: 'Ratenzahlung anbieten', icon: CalendarClock, to: '/kanzlei/zahlungen/raten', zahlung: true },
  { id: 'termin', label: 'Termin vorschlagen', icon: CalendarDays, to: '/kanzlei/termine' },
  { id: 'unterschrift', label: 'Unterschrift anfordern', icon: PenLine, to: '/kanzlei/mandatsunterlagen' },
  { id: 'einladung', label: 'Zur Mandantenerfassung einladen', icon: UserPlus },
  { id: 'aufgabe', label: 'Aufgabe anlegen', icon: ListChecks, to: '/team/tickets', alle: true },
];

// Öffnet das Anlage-Formular einer Seite, wenn die URL ?neu=… enthält
export const useNeuOeffnen = (oeffnen, formTestId) => {
  const [params] = useSearchParams();
  const neu = params.get('neu');
  useEffect(() => {
    if (!neu) return undefined;
    oeffnen(true);
    const t = formTestId && setTimeout(() => document.querySelector(`[data-testid="${formTestId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 400);
    return () => clearTimeout(t);
  }, [neu]); // eslint-disable-line react-hooks/exhaustive-deps
};

// Mandant aus ?mnr= (Schnellaktion beim Mandanten) für Formular-Vorbelegung
export const useMandantAusUrl = () => {
  const [params] = useSearchParams();
  const mnr = params.get('mnr');
  const [m, setM] = useState(null);
  useEffect(() => {
    if (!mnr) { setM(null); return; }
    api.get('/register/mandanten', { params: { q: mnr } }).then((r) => setM((r.data.items || []).find((x) => x.mandanten_nr === mnr) || null)).catch(() => setM(null));
  }, [mnr]);
  return m;
};

const ziel = (to, mnr) => `${to}${to.includes('?') ? '&' : '?'}neu=${Date.now()}${mnr ? `&mnr=${encodeURIComponent(mnr)}` : ''}`;

export const NeuMenu = ({ mnr, caseId, schnell = false, leiste = false, onAkte }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [einladen, setEinladen] = useState(false);
  const [dialog, setDialog] = useState(null);
  const freelancer = user?.role === 'freelancer';
  const items = AKTIONEN.filter((a) => !freelancer || a.alle).filter((a) => !(mnr && a.id === 'einladung')).filter((a) => !a.akte || caseId);
  const waehle = (a) => (a.id === 'einladung' ? setEinladen(true) : a.to ? navigate(ziel(a.to, mnr)) : setDialog(a.id));
  const tid = schnell ? 'schnellaktion' : 'neu';
  const knopf = leiste
    ? 'shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-xl text-[13px] font-semibold border border-[#DCE3EA] bg-white text-[#15202B] hover:border-[#b8893d] hover:-translate-y-0.5 hover:shadow-[0_10px_22px_-16px_rgba(11,23,36,.6)] transition-[transform,border-color,box-shadow] duration-200'
    : 'inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11.5px] border border-[#DCE3EA] bg-white text-[#1B3A5C] hover:border-[#b8893d] hover:bg-[#fbf7f0] transition-colors';
  return (
    <>
      {schnell || leiste ? (
        <div className={leiste ? 'flex flex-nowrap sm:flex-wrap gap-1.5 overflow-x-auto sm:overflow-visible no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 pt-2 pb-1' : 'flex flex-wrap gap-1.5'} data-testid={`${tid}-leiste`}>
          {leiste && <span className="hidden sm:block w-full text-[10px] tracking-[0.2em] uppercase text-[#7a8a99] mb-0.5">{mnr ? `Neu für Mandant ${mnr}` : 'Neu anlegen'}</span>}
          {items.map((a) => (
            <button key={a.id} type="button" onClick={() => waehle(a)} data-testid={`${tid}-${a.id}`} className={knopf}>
              <a.icon className="w-3.5 h-3.5 text-[#b8893d]" strokeWidth={1.75} /> {a.label}
            </button>
          ))}
        </div>
      ) : (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" data-testid={`${tid}-menu-btn`}
            className={schnell
              ? 'inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-wider border border-[#b8893d] text-[#1B3A5C] hover:bg-[#b8893d] hover:text-white rounded-[10px] transition-colors'
              : 'shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-[10px] bg-gradient-to-b from-[#e0b56a] to-[#b8893d] text-[#0B1724] text-[13px] font-semibold shadow-[0_0_0_1px_rgba(224,181,106,0.5),0_6px_18px_-8px_#e0b56a] hover:brightness-110 transition-[filter] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e0b56a]/70'}>
            <Plus className="w-4 h-4" strokeWidth={2.2} /> {schnell ? 'Schnellaktion' : 'Neu'}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64" data-testid={`${tid}-menu`}>
          <DropdownMenuLabel className="text-[10px] tracking-[0.2em] uppercase text-[#7a8a99]">{mnr ? `Für Mandant ${mnr}` : 'Neu anlegen'}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {items.map((a) => (
            <DropdownMenuItem key={a.id} onSelect={() => waehle(a)} data-testid={`${tid}-${a.id}`} className="gap-2 cursor-pointer">
              <a.icon className="w-4 h-4 text-[#b8893d]" strokeWidth={1.75} /> {a.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      )}
      {einladen && <ErfassungEinladenDialog onClose={() => setEinladen(false)} />}
      {dialog === 'akte' && <NewCaseModal mnr={mnr} onClose={() => setDialog(null)} onCreated={(c) => { setDialog(null); if (onAkte) onAkte(c); else navigate(`/team-portal/${c.id}`); }} />}
      <Dialog open={dialog === 'beteiligter'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-xl" data-testid="beteiligter-dialog">
          <DialogTitle className="font-serif text-xl text-[#1B3A5C]">Mandanten dieser Akte</DialogTitle>
          {caseId && <AkteMandanten caseId={caseId} tid="dialog-akte-mandant" />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NeuMenu;
